import {
  Avatar,
  Box,
  Button,
  Center,
  HStack,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuTrigger,
  Separator,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react"
import AlertSeverityBadge, { type AlertType, getAlertSeverityColor, formatAlertMessage } from "@hydrologiq/shared/components/AlertSeverityBadge"
import { toUserTZ, nowDateTime } from "@hydrologiq/common/helpers/date"
import { AlertAssetInfo, AssetKind } from "@hydrologiq/gql-experience/graphql"
import { AssetLink } from "@hydrologiq/shared/asset/AssetLink"
import Link from "@hydrologiq/shared/components/Link"
import { FaWarehouse, FaUsers, FaCog } from "react-icons/fa"
import {
  FaUserPlus,
  FaChartLine,
  FaArrowUpRightFromSquare,
  FaLocationDot,
  FaTowerBroadcast,
} from "react-icons/fa6"
import type { IconType } from "react-icons"
import { memo, useCallback, useMemo } from "react"
import {
  KANBAN_ASSIGNEE_AVATAR_FONT,
  KANBAN_ASSIGNEE_CIRCLE,
  KANBAN_CARD_P,
  KANBAN_CONTEXT_INDENT_PX,
} from "./responsiveConstants"
import { effectiveDisplaySystem } from "./kanbanAlertSections"
import { hasAlertDataContext } from "./types"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import { EditButton } from "@hydrologiq/shared/content/components/Button"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"

export interface AlertEvent {
  id: string
  kind: string
  note: string | null
  created_at: string
  created_by: string
  user?: {
    id: string
    email?: string
    name?: string
  } | null
}

export interface KanbanAssignee {
  id: string
  name?: string | null
  email?: string | null
}

export interface AssignableUser {
  id: string
  name?: string | null
  email?: string | null
}

export interface KanbanAlert {
  id: string
  name: string
  status: string
  severity: string
  message: string
  fired_at: string
  type?: string
  template_name?: string | null
  customer_site_context?: {
    customer_id?: string
    customer_name?: string
    site_id?: string
    site_name?: string
    system_id?: string
    system_name?: string
  } | null
  /** From alert_list_view when present; used for grouped kanban when context has no system */
  systems?: { id?: string; name?: string | null }[] | null
  installation_name?: string | null
  events?: AlertEvent[]
  latest_note?: string | null
  assets?: AlertAssetInfo[]
  assignee?: KanbanAssignee | null
  assigned_to?: string | null
  /** Metadata including optional data_context for "See Data" popover */
  metadata?: Record<string, unknown> | null
}

export interface AlertCardProps {
  alert: KanbanAlert
  onAttended?: (alertId: string) => void
  isUpdating?: boolean
  onClick?: () => void
  isPreview?: boolean
  assignableUsers?: AssignableUser[]
  onAssigneeChange?: (alertId: string, userId: string | null) => void | Promise<void>
  /** True while assignee mutation is in flight for this alert */
  isAssigneeSaving?: boolean
  /** Callback when "See Data" button is clicked */
  onSeeDataClick?: () => void
  /** Hide system + customer/site links (when viewing within a system) */
  systemView?: boolean
  /** Hide asset link (when viewing within an asset) */
  assetView?: boolean
  /** Whether this card is highlighted (e.g. after column switch) */
  isHighlighted?: boolean
}

function formatRelativeTime(firedAt: string): string {
  try {
    // Backend timestamps are UTC but arrive without a timezone suffix (e.g.
    // "2026-09-01T12:39:06.422289"). toUserTZ parses them as UTC and converts
    // to the user's zone; comparing absolute epoch millis avoids the offset
    // error that plain `new Date(firedAt)` (parsed as local) introduces.
    const fired = toUserTZ(firedAt)
    const now = nowDateTime()
    const diffMs = now.unixUtcMillis() - fired.unixUtcMillis()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "just now"
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    // Older than a week — show the absolute date instead of a relative label
    const isCurrentYear = fired.year() === now.year()
    return fired.format(isCurrentYear ? "dd MMM HH:mm" : "dd MMM yyyy")
  } catch {
    return firedAt
  }
}

/** Full absolute timestamp for tooltip hover on the relative time label. */
function formatAbsoluteTime(firedAt: string): string {
  try {
    return toUserTZ(firedAt).format("dd MMM yyyy HH:mm")
  } catch {
    return firedAt
  }
}

function userInitials(name?: string | null, email?: string | null): string {
  const n = (name || "").trim()
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return (
        (parts[0][0] || "") + (parts[parts.length - 1][0] || "")
      ).toUpperCase()
    }
    return n.slice(0, 2).toUpperCase()
  }
  const local = (email || "").split("@")[0] || "?"
  return local.slice(0, 2).toUpperCase()
}

function assignableLabel(u: {
  id: string
  name?: string | null
  email?: string | null
}): string {
  return (u.name || "").trim() || (u.email || "").trim() || u.id
}

function AssigneeInitialsAvatar({
  assignee: a,
  title: titleAttr,
}: {
  assignee: KanbanAssignee
  title: string
}) {
  return (
    <Avatar.Root
      colorPalette="gray"
      flexShrink={0}
      title={titleAttr}
      boxSize={KANBAN_ASSIGNEE_CIRCLE}
      minW={KANBAN_ASSIGNEE_CIRCLE}
      minH={KANBAN_ASSIGNEE_CIRCLE}
      fontSize={KANBAN_ASSIGNEE_AVATAR_FONT}
      bg="blue"
      color="white"
    >
      <Avatar.Fallback>{userInitials(a.name, a.email)}</Avatar.Fallback>
    </Avatar.Root>
  )
}

function UnassignedAssigneeAffordance({
  asTrigger = false,
  iconSize,
}: {
  asTrigger?: boolean
  iconSize: number
}) {
  return (
    <Box
      w={KANBAN_ASSIGNEE_CIRCLE}
      h={KANBAN_ASSIGNEE_CIRCLE}
      minW={KANBAN_ASSIGNEE_CIRCLE}
      minH={KANBAN_ASSIGNEE_CIRCLE}
      borderRadius="full"
      borderWidth="1px"
      borderStyle="dashed"
      borderColor="active"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="blue"
      color="white"
      flexShrink={0}
      boxSizing="border-box"
      aria-hidden={asTrigger ? undefined : true}
    >
      <FaUserPlus size={iconSize} aria-hidden />
    </Box>
  )
}

/**
 * One rung of the alert's Customer › Site › System › Asset hierarchy.
 *
 * A rung is clickable only when `href` is given; the open-in-new-tab affordance
 * stays hidden until the row is hovered, so a full chain doesn't stack up icons at rest.
 */
function ContextNode({
  label,
  icon: Glyph,
  href,
  moreCount = 0,
  children,
}: {
  label: string
  icon: IconType
  href?: string
  moreCount?: number
  children: React.ReactNode
}) {
  return (
    <HStack
      gap={1.5}
      align="center"
      minW={0}
      width="100%"
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        color="gray.400"
        aria-hidden
        mt="1px"
      >
        <Glyph size={12} />
      </Box>
      <Text fontSize="xs" color="gray.500" flexShrink={0} fontWeight="medium" w="72px" whiteSpace="nowrap">
        {label}:
      </Text>
      <HStack minW={0} flex={1} gap={1} align="center">
        <Box minW={0} truncate>
          {children}
        </Box>
        {href && (
          <Box
            as="button"
            className="ctx-open"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            p="2px"
            borderRadius="sm"
            color="primary"
            cursor="pointer"
            transition="all 0.15s ease"
            aria-label="Open in new tab"
            _hover={{
              color: "#39AED2",
              bg: "blue.50",
            }}
            onClick={(e) => {
              e.stopPropagation()
              window.open(href, "_blank", "noopener,noreferrer")
            }}
          >
            <FaArrowUpRightFromSquare size={12} />
          </Box>
        )}
      </HStack>
      {moreCount > 0 && (
        <Text fontSize="xs" color="gray.500" flexShrink={0} whiteSpace="nowrap">
          +{moreCount} more
        </Text>
      )}
    </HStack>
  )
}

function AlertCardInternal({
  alert,
  onAttended,
  isUpdating = false,
  onClick,
  isPreview: _isPreview = false,
  assignableUsers = [],
  onAssigneeChange,
  isAssigneeSaving = false,
  onSeeDataClick,
  systemView = false,
  assetView = false,
  isHighlighted = false,
}: AlertCardProps) {
  const isCustomersEnabled = useIsSectionEnabled("customers")
  const assigneeIconSize =
    useBreakpointValue({ base: 14, sm: 14, md: 12, "4xl": 14 }) ?? 12

  const status = alert.status
  const severity = (alert.severity || "info") as "critical" | "warning" | "info" | "ok"
  const alertType = (alert.type || "status") as AlertType
  const ctx = alert.customer_site_context

  const customerName = useMemo(() => ctx?.customer_name ?? "No customer", [ctx?.customer_name])
  const hasCustomer = useMemo(() => Boolean(ctx?.customer_id), [ctx?.customer_id])
  const hasSite = useMemo(
    () => Boolean(ctx?.site_id) && Boolean(ctx?.customer_id),
    [ctx?.site_id, ctx?.customer_id]
  )
  const displaySystem = useMemo(() => effectiveDisplaySystem(alert), [alert])
  const hasSystemDisplay = useMemo(() => Boolean(displaySystem.id), [displaySystem.id])

  const borderColor = useMemo(() => getAlertSeverityColor(severity), [severity])
  const asset = useMemo(() => alert.assets?.[0], [alert.assets])
  const displayName = alert.template_name || alert.name

  const extraSystemCount = useMemo(
    () => Math.max(0, (alert.systems?.length ?? 0) - 1),
    [alert.systems]
  )
  const extraAssetCount = useMemo(
    () => Math.max(0, (alert.assets?.length ?? 0) - 1),
    [alert.assets]
  )

  // Installation is a parallel lineage, not a rung — surface it only when the
  // alert has no customer/site/system context to show at all.
  const showInstallation = useMemo(
    () => !systemView && !hasCustomer && !hasSystemDisplay && Boolean(alert.installation_name),
    [systemView, hasCustomer, hasSystemDisplay, alert.installation_name]
  )
  const hasActions = useMemo(() => status === "acknowledged" && Boolean(onAttended), [status, onAttended])
  const hasDataContext = useMemo(() => hasAlertDataContext(alert.metadata), [alert.metadata])

  const assignee = alert.assignee
  const showAssigneePicker = Boolean(onAssigneeChange)

  const handlePickAssignee = useCallback(async (userId: string | null) => {
    if (!onAssigneeChange) return
    await onAssigneeChange(alert.id, userId)
  }, [onAssigneeChange, alert.id])

  const formattedFiredAt = useMemo(() => formatRelativeTime(alert.fired_at), [alert.fired_at])
  const absoluteFiredAt = useMemo(() => formatAbsoluteTime(alert.fired_at), [alert.fired_at])

  // Built top-down so a rung's position matches the domain hierarchy.
  const contextRungs: {
    key: string
    label: string
    icon: IconType
    href?: string
    moreCount?: number
    content: React.ReactNode
  }[] = []

  if (!systemView && hasCustomer) {
    contextRungs.push({
      key: "customer",
      label: "Customer",
      icon: FaUsers,
      href: isCustomersEnabled ? `/customers/${ctx?.customer_id}` : undefined,
      content: (
        <Link
          href={`/customers/${ctx?.customer_id}`}
          internal
          fontSize="sm"
          truncate
          maxWidth="100%"
          deLink={!isCustomersEnabled}
        >
          {customerName}
        </Link>
      ),
    })
  }

  if (!systemView && hasSite) {
    contextRungs.push({
      key: "site",
      label: "Site",
      icon: FaLocationDot,
      href: isCustomersEnabled
        ? `/customers/${ctx?.customer_id}/sites/${ctx?.site_id}`
        : undefined,
      content: (
        <Link
          href={`/customers/${ctx?.customer_id}/sites/${ctx?.site_id}`}
          internal
          fontSize="sm"
          truncate
          maxWidth="100%"
          deLink={!isCustomersEnabled}
        >
          {ctx?.site_name ?? "Site"}
        </Link>
      ),
    })
  }

  if (!systemView && hasSystemDisplay && displaySystem.id) {
    contextRungs.push({
      key: "system",
      label: "System",
      icon: FaCog,
      href: `/systems/${displaySystem.id}`,
      moreCount: extraSystemCount,
      content: (
        <Link href={`/systems/${displaySystem.id}`} internal fontSize="sm" truncate maxWidth="100%">
          {displaySystem.name}
        </Link>
      ),
    })
  }

  if (showInstallation) {
    contextRungs.push({
      key: "installation",
      label: "Installation",
      icon: FaTowerBroadcast,
      content: <Text fontSize="sm" truncate>{alert.installation_name}</Text>,
    })
  }

  if (!assetView && asset) {
    contextRungs.push({
      key: "asset",
      label: "Asset",
      icon: FaWarehouse,
      href: `/assets/${asset.id}`,
      moreCount: extraAssetCount,
      content: (
        <AssetLink
          openExternal
          internal
          asset={{
            id: asset.id,
            name: asset.name ?? "",
            kind: (asset.kind as AssetKind) ?? "",
            monitoring_status:
              (asset as { monitoring_status?: string | null }).monitoring_status ?? "inactive",
          }}
          size="sm"
          truncate
          maxWidth="100%"
        />
      ),
    })
  }

  return (
    <Box
      p={KANBAN_CARD_P}
      borderRadius="md"
      bg="white"
      boxShadow="card"
      borderWidth={isHighlighted ? "2px" : "1px"}
      borderStyle="solid"
      borderColor={isHighlighted ? "active" : "gray.200"}
      borderLeftWidth={isHighlighted ? "2px" : { base: "4px", md: "3px" }}
      borderLeftStyle="solid"
      borderLeftColor={isHighlighted ? "active" : borderColor}
      _hover={{ bg: "grey_light" }}
      transition="background 0.15s, border-color 0.2s"
      minW={0}
      maxW="100%"
    >
      <VStack align="stretch" gap={{ base: 2, md: 1.5 }} width="100%" minW={0}>
        {/* Row 1: Severity badge + type | action icons */}
        <HStack width="100%" justify="space-between" align="center" minW={0}>
          <HStack gap={2} align="center" minW={0} flex={1}>
            <AlertSeverityBadge severity={severity} type={alertType} compact />
          </HStack>
          <HStack gap={2} flexShrink={0} align="center">
            {onClick && (
              <EditButton
                size="xs"
                onClick={(e) => {
                  e.stopPropagation()
                  onClick()
                }}
                aria-label="Edit alert"
              />
            )}
            {showAssigneePicker && (
              <MenuRoot>
                <MenuTrigger asChild>
                  <Box
                    as="button"
                    focusVisibleRing="none"
                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                    p={0}
                    m={0}
                    w={KANBAN_ASSIGNEE_CIRCLE}
                    h={KANBAN_ASSIGNEE_CIRCLE}
                    minW={KANBAN_ASSIGNEE_CIRCLE}
                    minH={KANBAN_ASSIGNEE_CIRCLE}
                    lineHeight={0}
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                    flexShrink={0}
                    bg="transparent"
                    border="none"
                    aria-disabled={isAssigneeSaving}
                    pointerEvents={isAssigneeSaving ? "none" : "auto"}
                    aria-busy={isAssigneeSaving}
                    aria-label={
                      isAssigneeSaving
                        ? "Updating assignee"
                        : assignee
                          ? `Change assignee (${assignableLabel(assignee)})`
                          : "Assign or change assignee (unassigned)"
                    }
                    cursor={isAssigneeSaving ? "default" : "pointer"}
                    opacity={isAssigneeSaving ? 0.85 : 1}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {isAssigneeSaving ? (
                      <Center
                        w={KANBAN_ASSIGNEE_CIRCLE}
                        h={KANBAN_ASSIGNEE_CIRCLE}
                        minW={KANBAN_ASSIGNEE_CIRCLE}
                        minH={KANBAN_ASSIGNEE_CIRCLE}
                        flexShrink={0}
                        aria-hidden
                      >
                        <Spinner size="xs" />
                      </Center>
                    ) : assignee ? (
                      <AssigneeInitialsAvatar
                        assignee={assignee}
                        title={assignableLabel(assignee)}
                      />
                    ) : (
                      <UnassignedAssigneeAffordance asTrigger iconSize={assigneeIconSize} />
                    )}
                  </Box>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent maxH="240px" overflowY="auto" minW="200px">
                    <MenuItem
                      value="unassigned"
                      disabled={isUpdating || isAssigneeSaving}
                      onClick={() => void handlePickAssignee(null)}
                    >
                      Unassigned
                    </MenuItem>
                    {assignableUsers.length === 0 ? (
                      <MenuItem value="no-users" disabled>
                        No other users available
                      </MenuItem>
                    ) : (
                      assignableUsers.map((u) => (
                        <MenuItem
                          key={u.id}
                          value={u.id}
                          disabled={isUpdating || isAssigneeSaving}
                          onClick={() => void handlePickAssignee(u.id)}
                        >
                          {assignableLabel(u)}
                        </MenuItem>
                      ))
                    )}
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>
            )}
            {!showAssigneePicker && assignee && (
              <AssigneeInitialsAvatar
                assignee={assignee}
                title={assignableLabel(assignee)}
              />
            )}
            {!showAssigneePicker && !assignee && (
              <Box aria-label="Unassigned" title="Unassigned">
                <UnassignedAssigneeAffordance iconSize={assigneeIconSize} />
              </Box>
            )}
          </HStack>
        </HStack>

        {/* Row 2: Template name (bold heading) */}
        <Text
          fontWeight="bold"
          fontSize={{ base: "md", md: "lg", "2xl": "xl" }}
          lineHeight="short"
          maxLines={2}
        >
          {displayName}
        </Text>

        {/* Row 3: Message */}
        {alert.message && (
          <Text
            fontSize={{ base: "sm", md: "sm" }}
            lineHeight="tall"
            maxLines={{ base: 4, md: 2 }}
            whiteSpace="pre-wrap"
          >
            {formatAlertMessage(alert.message)}
          </Text>
        )}

        {/* Timestamp */}
        <Tooltip content={absoluteFiredAt} positioning={{ placement: "bottom" }}>
          <Text
            fontSize={{ base: "xs", md: "sm" }}
            color="gray.500"
            whiteSpace="nowrap"
          >
            {formattedFiredAt}
          </Text>
        </Tooltip>

        {/* Context: Customer › Site › System › Asset, top-down, missing rungs omitted */}
        {contextRungs.length > 0 && (
          <>
            <Separator borderColor="gray.200" />

            <VStack align="stretch" gap={{ base: 1, md: 0.5 }} minW={0}>
              {contextRungs.map((rung) => (
                <ContextNode
                  key={rung.key}
                  label={rung.label}
                  icon={rung.icon}
                  href={rung.href}
                  moreCount={rung.moreCount}
                >
                  {rung.content}
                </ContextNode>
              ))}
            </VStack>
          </>
        )}

        {/* Action buttons */}
        {(hasDataContext && onSeeDataClick || (hasActions && onAttended) || status === "attended") && (
          <HStack gap={2} width="100%" pt={1}>
            {hasDataContext && onSeeDataClick && (
              <Button
                size={{ base: "sm", md: "xs" }}
                variant="hydrologiq_primary"
                onClick={(e) => {
                  e.stopPropagation()
                  onSeeDataClick()
                }}
              >
                <FaChartLine />
                <Text ml={1}>View data</Text>
              </Button>
            )}
            {hasActions && onAttended && (
              <Button
                size={{ base: "sm", md: "xs" }}
                variant="hydrologiq_secondary"
                onClick={() => onAttended(alert.id)}
                loading={isUpdating}
                disabled={isUpdating}
              >
                Mark attended
              </Button>
            )}
            {status === "attended" && (
              <Text
                color="green"
                fontWeight="semibold"
                fontSize={{ base: "sm", md: "sm" }}
              >
                Attended
              </Text>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  )
}

export function areAlertsEqual(prevProps: AlertCardProps, nextProps: AlertCardProps): boolean {
  const prev = prevProps.alert
  const next = nextProps.alert

  if (prev.id !== next.id) return false
  if (prev.status !== next.status) return false
  if (prev.severity !== next.severity) return false
  if (prev.name !== next.name) return false
  if (prev.template_name !== next.template_name) return false
  if (prev.message !== next.message) return false
  if (prev.fired_at !== next.fired_at) return false
  if (prevProps.isUpdating !== nextProps.isUpdating) return false
  if (prevProps.isAssigneeSaving !== nextProps.isAssigneeSaving) return false
  if (prevProps.onClick !== nextProps.onClick) return false
  if (prevProps.onAttended !== nextProps.onAttended) return false
  if (prevProps.onAssigneeChange !== nextProps.onAssigneeChange) return false
  if (prevProps.onSeeDataClick !== nextProps.onSeeDataClick) return false
  if (prevProps.isPreview !== nextProps.isPreview) return false
  if (prevProps.isHighlighted !== nextProps.isHighlighted) return false

  const prevAssigneeId = prev.assignee?.id ?? prev.assigned_to ?? null
  const nextAssigneeId = next.assignee?.id ?? next.assigned_to ?? null
  if (prevAssigneeId !== nextAssigneeId) return false

  const prevAssetId = prev.assets?.[0]?.id
  const nextAssetId = next.assets?.[0]?.id
  if (prevAssetId !== nextAssetId) return false

  // Context rungs: counts drive the "+N more" suffixes, systems[0] and
  // installation_name are rendered when the context has no system/customer.
  if ((prev.assets?.length ?? 0) !== (next.assets?.length ?? 0)) return false
  if ((prev.systems?.length ?? 0) !== (next.systems?.length ?? 0)) return false
  if (prev.systems?.[0]?.id !== next.systems?.[0]?.id) return false
  if (prev.systems?.[0]?.name !== next.systems?.[0]?.name) return false
  if (prev.installation_name !== next.installation_name) return false

  if (prev.customer_site_context?.customer_id !== next.customer_site_context?.customer_id) return false
  if (prev.customer_site_context?.customer_name !== next.customer_site_context?.customer_name) return false
  if (prev.customer_site_context?.site_id !== next.customer_site_context?.site_id) return false
  if (prev.customer_site_context?.site_name !== next.customer_site_context?.site_name) return false
  if (prev.customer_site_context?.system_id !== next.customer_site_context?.system_id) return false
  if (prev.customer_site_context?.system_name !== next.customer_site_context?.system_name) return false

  if (prevProps.assignableUsers?.length !== nextProps.assignableUsers?.length) return false

  return true
}

export const AlertCard = memo(AlertCardInternal, areAlertsEqual)
